import React, {useState, useEffect, useRef} from 'react';
import ReactCSSTransitionGroup from 'react-addons-css-transition-group';
import {Button} from 'web-components';

import SadFace from '../../images/emojis/sad_face.png';
import FrowningFace from '../../images/emojis/frowning_face.png';
import NeutralFace from '../../images/emojis/neutral_face.png';
import SmilingFace from '../../images/emojis/smiling_face.png';
import GrinningFace from '../../images/emojis/grinning_face.png';

import {Emoji, EmojiContainer, NpsWrapper, NpsTitle, CloseButton, NpsContent, FlexCol, ButtonWrapper, ReviewButtonWrapper, NumberContainer, NumberField, NpsFooter, NpsFooterText, CelebratingGuy} from './NpsStyled';

import TooltipContainer from '../../forecast-app/shared/components/tooltips/tooltip_container';
import InputField from '../new-ui/input_field';
import {NpsTypes} from '../../constants';
import Util from '../../forecast-app/shared/util/util';
import CreateNpsMutation from '../../mutations/create_nps_mutation';

import * as tracking from '../../tracking';
const getNpsType = pathname => {
	if (pathname.endsWith('/overview') && (pathname.includes('/connected') || pathname.includes('/project'))) {
		return NpsTypes.OVERVIEW;
	} else {
		return NpsTypes.NPS_EMOJI;
	}
};

const Nps = ({npsShouldShow, cesOverviewShouldShow, personId}) => {
	const [isEmojiNps] = useState(true);
	const [npsType, setNpsType] = useState(getNpsType(window.location.pathname));
	const [step, setStep] = useState(0); // Step 0: hidden. Step 1: score. Step 2: comment. Step 3: thank you.
	const [score, setScore] = useState(null);
	const [comment, setComment] = useState('');

	const npsSubmitted = useRef(false); // Don't submit another NPS if user clicks X after submitting...
	const showReview = (isEmojiNps && score > 4) || score > 8;

	const getNpsShouldShow = NpsType => {
		switch (NpsType) {
			case NpsTypes.OVERVIEW:
				return cesOverviewShouldShow;
			default:
				return npsShouldShow;
		}
	};

	useEffect(() => {
		if (getNpsShouldShow(npsType)) {
			const timeout = setTimeout(() => {
				tracking.trackSurvey({event: 'SHOW', type: npsType, personId: personId});
				setStep(1);
			}, 60000);
			return () => {
				clearTimeout(timeout);
			};
		} else {
			setStep(0);
		}
	}, [npsType]);

	useEffect(() => {
		const pathname = window.location.pathname;
		setNpsType(getNpsType(pathname));
	}, [window.location.pathname]);

	const selectScore = score => {
		setScore(score);
		setStep(2);
	};

	const createNps = () => {
		const onSubmit = () => {
			if (score != null) {
				tracking.trackSurvey({event: 'ANSWER', type: npsType, personId: personId, score: score, comment: comment});
				if (showReview) {
					setStep(3);
					setTimeout(() => {
						setStep(0);
					}, 5000);
				} else {
					setStep(3);
					setTimeout(() => {
						setStep(0);
					}, 1500);
				}
			} else {
				setStep(0);
			}
		};
		if (!npsSubmitted.current) {
			npsSubmitted.current = true;

			Util.CommitMutation(CreateNpsMutation, {
				personId: personId,
				score,
				comment,
				type: npsType,
			});
			onSubmit();
		} else {
			setStep(0);
		}
	};

	const submitComment = () => {
		if (comment.length) {
			createNps();
		}
	};

	const goToReview = () => {
		tracking.trackSurvey({event: 'REVIEW', type: npsType, personId: personId, score: score, comment: comment});
		window.open('https://reviews.capterra.com/new/138122');
	};

	const getNpsQuestion = () => {
		switch (npsType) {
			case NpsTypes.OVERVIEW:
				return 'How easy is it to get an overview of the project?';
			default:
				return "What's your overall experience with Forecast?";
		}
	};

	const getNpsTitle = step => {
		switch (step) {
			case 1:
				return <NpsTitle centerText={true}>{getNpsQuestion()}</NpsTitle>;
			case 2:
				return <NpsTitle>{'Why do you feel that way?'}</NpsTitle>;
			case 3:
				return (
					<NpsTitle fontSize={'24px'} centerText={true}>
						{'Thank you!'}
					</NpsTitle>
				);
			default:
				return null;
		}
	};

	const getEmojiSection = () => {
		const infoTextArray = npsType === NpsTypes.NPS_EMOJI ? ['I hate it.', 'I dislike it.', "I don't know", 'I like it.', 'I love it!'] : ['Very hard', 'Hard', "Don't know", 'Easy', 'Very easy'];
		return (
			<EmojiContainer>
				<TooltipContainer infoText={infoTextArray[0]} tooltipDuration={1000000000} canBeMiddle={true}>
					<Emoji imageSrc={SadFace} onClick={selectScore.bind(this, 1)} />
				</TooltipContainer>
				<TooltipContainer infoText={infoTextArray[1]} tooltipDuration={1000000000} canBeMiddle={true}>
					<Emoji imageSrc={FrowningFace} onClick={selectScore.bind(this, 2)} />
				</TooltipContainer>
				<TooltipContainer infoText={infoTextArray[2]} tooltipDuration={1000000000} canBeMiddle={true}>
					<Emoji imageSrc={NeutralFace} onClick={selectScore.bind(this, 3)} />
				</TooltipContainer>
				<TooltipContainer infoText={infoTextArray[3]} tooltipDuration={1000000000} canBeMiddle={true}>
					<Emoji imageSrc={SmilingFace} onClick={selectScore.bind(this, 4)} />
				</TooltipContainer>
				<TooltipContainer infoText={infoTextArray[4]} tooltipDuration={1000000000} canBeMiddle={true}>
					<Emoji imageSrc={GrinningFace} onClick={selectScore.bind(this, 5)} />
				</TooltipContainer>
			</EmojiContainer>
		);
	};

	const getNumbersSection = () => {
		const scores = [...Array(11).keys()];
		return (
			<FlexCol>
				<NumberContainer>
					{scores.map(score => {
						return <NumberField onClick={selectScore.bind(this, score)}>{score}</NumberField>;
					})}
				</NumberContainer>
				<NpsFooter>
					<NpsFooterText>0 = not likely at all</NpsFooterText>
					<NpsFooterText>10 = extremely likely</NpsFooterText>
				</NpsFooter>
			</FlexCol>
		);
	};

	const getNpsContent = step => {
		switch (step) {
			case 1:
				return isEmojiNps ? getEmojiSection() : getNumbersSection();
			case 2:
				return (
					<FlexCol>
						<InputField autoFocus={true} growable={true} label="" value={comment} onChange={comment => setComment(comment)} type="text" placeholder={'Write a comment...'} onEnter={submitComment} hideLabel={true} />
						<ButtonWrapper>
							<Button variant={Button.VARIANT.GREEN_FILLED} onClick={createNps.bind(this)}>
								SUBMIT
							</Button>
						</ButtonWrapper>
					</FlexCol>
				);
			case 3:
				return (
					<FlexCol>
						<CelebratingGuy />
						{showReview ? (
							<>
								<NpsTitle centerText={true}>Would you like to write a review about us?</NpsTitle>
								<ReviewButtonWrapper>
									<Button variant={Button.VARIANT.GREEN_FILLED} onClick={goToReview.bind(this)}>
										Write a Review
									</Button>
								</ReviewButtonWrapper>
							</>
						) : null}
					</FlexCol>
				);
			default:
				return null;
		}
	};

	return (
		<ReactCSSTransitionGroup transitionName="nps" transitionAppear={true} transitionAppearTimeout={500} transitionEnterTimeout={500} transitionLeaveTimeout={300}>
			{step === 0 ? null : (
				<NpsWrapper extraPadded={(isEmojiNps && step === 1) || step === 3}>
					<CloseButton onClick={createNps.bind(this)} />
					{getNpsTitle(step)}
					<NpsContent>{getNpsContent(step)}</NpsContent>
				</NpsWrapper>
			)}
		</ReactCSSTransitionGroup>
	);
};

export default Nps;
